import { Play, Upload, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  desktopSupported,
  getPrefs,
  onPrefsChange,
  playSound,
  requestDesktopPermission,
  setCustomSound,
  setPrefs,
  SoundId,
  SOUNDS,
} from '../app/notifications';
import { errorMessage } from '../lib/api';
import { PushSettings } from './PushSettings';
import { toast } from './toasts';

/** Som e notificações do sistema (preferências deste navegador). */
export function NotifyMenu() {
  const [open, setOpen] = useState(false);
  const [prefs, set] = useState(getPrefs);
  const [permission, setPermission] = useState(desktopSupported() ? Notification.permission : 'denied');
  const fileInput = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => onPrefsChange(() => set(getPrefs())), []);

  // Fecha ao clicar fora (o menu tem campos: não fecha ao passar o mouse).
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);

  const toggleDesktop = async (on: boolean) => {
    if (on && desktopSupported() && Notification.permission === 'default') setPermission(await requestDesktopPermission());
    setPrefs({ ...prefs, desktop: on });
  };

  const chooseSound = (id: SoundId) => {
    if (id === 'custom' && !prefs.customName) {
      fileInput.current?.click();
      return;
    }
    setPrefs({ ...prefs, soundId: id });
    setTimeout(() => playSound('message', true), 30);
  };

  const muted = !prefs.sound && !prefs.desktop;
  return (
    <div className="relative" ref={box}>
      <button className="rounded p-1.5 text-fg-soft hover:bg-muted" onClick={() => setOpen((o) => !o)} title="Som e notificações">
        {muted ? <VolumeX className="size-4" /> : <Volume2 className="size-4" />}
      </button>
      {open && (
        <div className="fixed inset-x-2 top-14 z-30 max-h-[80dvh] overflow-y-auto rounded-md md:absolute md:inset-x-auto md:top-auto md:right-0 md:mt-1 md:w-80 border border-border bg-panel p-3 text-sm shadow-lg">
          <label className="flex items-center gap-2 py-1">
            <input type="checkbox" checked={prefs.sound} onChange={(e) => setPrefs({ ...prefs, sound: e.target.checked })} />
            Som para mensagens e fila
          </label>

          <div className={prefs.sound ? '' : 'pointer-events-none opacity-50'}>
            <div className="mt-2 mb-1 text-xs font-medium text-fg-soft">Som</div>
            <div className="grid grid-cols-2 gap-1">
              {SOUNDS.map((s) => (
                <label key={s.id} className="flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-1 hover:bg-muted">
                  <input type="radio" name="atd-sound" checked={prefs.soundId === s.id} onChange={() => chooseSound(s.id)} />
                  <span className="truncate">{s.id === 'custom' && prefs.customName ? prefs.customName : s.label}</span>
                </label>
              ))}
            </div>
            {prefs.soundId === 'custom' && (
              <button className="mt-1 flex items-center gap-1 text-xs text-accent hover:underline" onClick={() => fileInput.current?.click()}>
                <Upload className="size-3" /> trocar arquivo (mp3, ogg ou wav, até 1 MB)
              </button>
            )}
            <input
              ref={fileInput}
              type="file"
              accept="audio/*"
              className="hidden"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                try {
                  await setCustomSound(file);
                  playSound('message', true);
                } catch (err) {
                  toast(errorMessage(err), 'error');
                }
              }}
            />
            <div className="mt-2 flex items-center gap-2">
              <span className="text-xs text-fg-soft">Volume</span>
              <input
                type="range"
                min={0.05}
                max={1}
                step={0.05}
                value={prefs.volume}
                onChange={(e) => setPrefs({ ...prefs, volume: Number(e.target.value) })}
                onMouseUp={() => playSound('message', true)}
                className="flex-1"
              />
              <button className="rounded p-1 text-fg-soft hover:bg-muted" onClick={() => playSound('message', true)} title="Testar">
                <Play className="size-3.5" />
              </button>
            </div>
          </div>

          <hr className="my-2 border-border" />
          <label className="flex items-center gap-2 py-1">
            <input type="checkbox" checked={prefs.desktop} disabled={!desktopSupported()} onChange={(e) => void toggleDesktop(e.target.checked)} />
            Notificações do sistema (com a janela em segundo plano)
          </label>
          {permission === 'denied' && prefs.desktop && (
            <p className="mt-1 text-xs text-warn">O navegador bloqueou as notificações deste site. Libere nas permissões do site (ícone ao lado do endereço).</p>
          )}
          {permission === 'default' && prefs.desktop && (
            <button className="mt-1 text-xs text-accent hover:underline" onClick={() => void requestDesktopPermission().then(setPermission)}>
              Permitir notificações neste navegador
            </button>
          )}
          <hr className="my-2 border-border" />
          <PushSettings />
          <p className="mt-2 text-[11px] text-fg-soft">Preferências deste navegador.</p>
        </div>
      )}
    </div>
  );
}
